import {
  Vector3,
  Vector2,
  TransformNode,
  Scene,
} from "babylonjs";
import appEvents from "@core/events/events";

// Constants
const VELOCITY_EASE = 0.3;
const VELOCITY_DECAY = 0.045;
const VELOCITY_THRESHOLD = 0.0005;
const EVENT_THRESHOLD_ROTATION = 0.05;
const TAP_MAX_DISTANCE_PX = 15;

export interface GestureOptions {
  /** Disable all gestures */
  disabled?: boolean;
  /** Min scale limit */
  minScale?: number;
  /** Max scale limit */
  maxScale?: number;
  /** Rotation speed multiplier */
  rotationSpeed?: number;
  /** Invert rotation direction (use true for 8th Wall) */
  invertRotation?: boolean;
  /** Pixels to move before locking to rotate or pan */
  lockThresholdPx?: number;
  /** Pan sensitivity multiplier */
  panSensitivity?: number;
  /** Disable one-finger pan (vertical drag moves Y) */
  disableOneFingerPan?: boolean;
  /** Disable two-finger pan */
  disableTwoFingerPan?: boolean;
  /** Disable scaling (pinch gesture) */
  disableScale?: boolean;
  /** Disable rotation (one-finger drag) */
  disableRotation?: boolean;
}

type GestureMode = 'none' | 'rotate' | 'panY';

/**
 * GestureController - Handles touch/pointer gestures for content manipulation
 * 
 * Uses DOM events (works in both 8th Wall and WebXR with DOM overlay)
 * 
 * Supports:
 * - One-finger drag: rotate (default) or pan Y
 * - Two-finger pinch: scale
 * - Two-finger drag: pan Y
 * - Tap detection (reports tap position)
 */
export class GestureController {
  private scene?: Scene;
  private contentNode?: TransformNode;
  private rotatableNodes: TransformNode[] = []; // Nodes to rotate based on arGestureOptions
  private targetElement?: HTMLElement;
  private options: Required<GestureOptions>;
  
  private isEnabled = false;
  private renderObserver?: any;
  
  // Bound event handlers for cleanup
  private boundPointerDown?: (ev: PointerEvent) => void;
  private boundPointerMove?: (ev: PointerEvent) => void;
  private boundPointerUp?: (ev: PointerEvent) => void;
  
  // Pointer tracking
  private activePointers = new Map<number, Vector2>();
  private tapStartPos: Vector2 | null = null;
  
  // Gesture state
  private gestureMode: GestureMode = 'none';
  private gestureStartPos = new Vector2(0, 0);
  private dragPos = new Vector2(0, 0);
  private dragVel = new Vector2(0, 0);
  private dragVelEased = new Vector2(0, 0);
  private dragging = false;
  
  // Scale state
  private initialScale: Vector3 | null = null;
  private initialPinchDistance = 0;
  private lastMidpointY: number | null = null;
  
  // Inertia
  private lastUpdateTime: number | null = null;
  private lastRotationY = 0;
  
  // Tap callback
  private onTapCallback?: (screenX: number, screenY: number) => void;

  constructor(options?: GestureOptions) {
    const o = options ?? {};
    this.options = {
      disabled: o.disabled ?? false,
      minScale: o.minScale ?? 0.07,
      maxScale: o.maxScale ?? 4,
      rotationSpeed: o.rotationSpeed ?? 0.0065,
      invertRotation: o.invertRotation ?? false,
      lockThresholdPx: o.lockThresholdPx ?? 10,
      panSensitivity: o.panSensitivity ?? 0.6,
      disableOneFingerPan: o.disableOneFingerPan ?? true,
      disableTwoFingerPan: o.disableTwoFingerPan ?? true,
      disableScale: o.disableScale ?? false,
      disableRotation: o.disableRotation ?? false,
    };
  }

  /**
   * Initialize with scene and content node
   */
  public initialize(scene: Scene, contentNode: TransformNode): void {
    this.scene = scene;
    this.contentNode = contentNode;
  }

  /**
   * Update gesture options at runtime
   */
  public setOptions(options: Partial<GestureOptions>): void {
    Object.assign(this.options, options);
  }

  /**
   * Set which nodes rotate based on their arGestureOptions
   * Nodes with disableRotation: true are excluded
   */
  public setRotatableNodes(nodes: TransformNode[]): void {
    this.rotatableNodes = nodes;
  }


  /**
   * Set the target element for gesture events
   * Call this before enable() or pass to enable(element)
   */
  public setTargetElement(element: HTMLElement): void {
    this.targetElement = element;
  }

  /**
   * Enable gesture handling on target element
   * @param element Optional - DOM element to attach listeners to (overlay or canvas)
   */
  public enable(element?: HTMLElement): void {
    if (this.isEnabled || this.options.disabled) return;
    
    // Use provided element, stored element, or fall back to scene's canvas
    const target = element ?? this.targetElement ?? this.scene?.getEngine().getRenderingCanvas();
    if (!target) return;
    
    this.targetElement = target;
    
    // Create bound handlers
    this.boundPointerDown = this.onPointerDown.bind(this);
    this.boundPointerMove = this.onPointerMove.bind(this);
    this.boundPointerUp = this.onPointerUp.bind(this);
    
    // Add DOM event listeners
    target.addEventListener('pointerdown', this.boundPointerDown);
    target.addEventListener('pointermove', this.boundPointerMove);
    target.addEventListener('pointerup', this.boundPointerUp);
    target.addEventListener('pointercancel', this.boundPointerUp);
    
    // Use scene render loop for inertia updates
    if (this.scene) {
      this.renderObserver = this.scene.onBeforeRenderObservable.add(() => {
        this.updateInertia();
      });
    }
    
    this.isEnabled = true;
  }

  /**
   * Disable gesture handling
   */
  public disable(): void {
    if (!this.isEnabled) return;
    
    // Remove DOM event listeners
    if (this.targetElement && this.boundPointerDown) {
      this.targetElement.removeEventListener('pointerdown', this.boundPointerDown);
      this.targetElement.removeEventListener('pointermove', this.boundPointerMove!);
      this.targetElement.removeEventListener('pointerup', this.boundPointerUp!);
      this.targetElement.removeEventListener('pointercancel', this.boundPointerUp!);
    }
    
    this.boundPointerDown = undefined;
    this.boundPointerMove = undefined;
    this.boundPointerUp = undefined;
    
    // Remove render observer
    if (this.renderObserver && this.scene) {
      this.scene.onBeforeRenderObservable.remove(this.renderObserver);
      this.renderObserver = undefined;
    }
    
    this.resetState();
    this.isEnabled = false;
  }

  /**
   * Set callback for tap events
   */
  public setOnTap(callback: (screenX: number, screenY: number) => void): void {
    this.onTapCallback = callback;
  }

  /**
   * Reset gesture state
   */
  public resetState(): void {
    this.activePointers.clear();
    this.tapStartPos = null;
    this.gestureMode = 'none';
    this.dragging = false;
    this.dragVel.set(0, 0);
    this.dragVelEased.set(0, 0);
    this.initialScale = null;
    this.initialPinchDistance = 0;
    this.lastMidpointY = null;
    this.lastUpdateTime = null;
  }

  /**
   * Dispose of resources
   */
  public dispose(): void {
    this.disable();
    this.targetElement = undefined;
    this.contentNode = undefined;
    this.scene = undefined;
    this.onTapCallback = undefined;
  }

  // ─────────────────────────────────────────────────────────────
  // Pointer Events (DOM-based)
  // ─────────────────────────────────────────────────────────────

  private onPointerDown(ev: PointerEvent): void {
    // Ignore events on buttons to allow UI interaction
    const target = ev.target as HTMLElement;
    if (target?.closest('button') || target?.tagName === 'BUTTON') {
      return;
    }
    
    const pos = new Vector2(ev.clientX, ev.clientY);
    
    // Track tap start for tap detection
    if (this.activePointers.size === 0 && ev.isPrimary) {
      this.tapStartPos = pos.clone();
    }
    
    this.activePointers.set(ev.pointerId, pos);

    if (this.activePointers.size === 1) {
      this.dragging = true;
      this.dragPos.copyFrom(pos);
      this.dragVel.set(0, 0);
      this.dragVelEased.set(0, 0);
      this.gestureMode = 'none';
      this.gestureStartPos.copyFrom(pos);
    } else if (this.activePointers.size === 2) {
      const [a, b] = Array.from(this.activePointers.values());
      this.initialPinchDistance = Vector2.Distance(a, b);
      this.initialScale = this.contentNode?.scaling.clone() || null;
      this.lastMidpointY = (a.y + b.y) * 0.5;
      this.dragging = false;
    }
  }

  private onPointerMove(ev: PointerEvent): void {
    if (!this.activePointers.has(ev.pointerId)) return;
    if (!this.contentNode) return;

    const current = new Vector2(ev.clientX, ev.clientY);
    this.activePointers.set(ev.pointerId, current);

    if (this.activePointers.size === 1 && this.dragging) {
      this.handleOneFingerGesture(current);
    } else if (this.activePointers.size === 2) {
      this.handleTwoFingerGesture();
    }
  }

  private onPointerUp(ev: PointerEvent): void {
    const pointerPos = this.activePointers.get(ev.pointerId);
    
    // Check for tap
    if (this.tapStartPos && pointerPos && ev.isPrimary) {
      const moveDistance = Vector2.Distance(this.tapStartPos, pointerPos);
      if (moveDistance < TAP_MAX_DISTANCE_PX) {
        this.onTapCallback?.(ev.clientX, ev.clientY);
      }
    }
    
    this.activePointers.delete(ev.pointerId);
    this.tapStartPos = null;

    if (this.activePointers.size < 2) {
      this.initialScale = null;
      this.initialPinchDistance = 0;
      this.lastMidpointY = null;
    }

    if (this.activePointers.size === 0) {
      this.dragging = false;
      this.gestureMode = 'none';
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Gesture Handling
  // ─────────────────────────────────────────────────────────────

  private handleOneFingerGesture(current: Vector2): void {
    if (!this.contentNode) return;

    const deltaX = current.x - this.dragPos.x;
    const deltaY = current.y - this.dragPos.y;

    // Lock gesture mode after threshold
    if (this.gestureMode === 'none') {
      const dx = Math.abs(current.x - this.gestureStartPos.x);
      const dy = Math.abs(current.y - this.gestureStartPos.y);
      
      if (dx < this.options.lockThresholdPx && dy < this.options.lockThresholdPx) {
        this.dragPos.copyFrom(current);
        return;
      }
      
      this.gestureMode = (dy > dx * 1.25 && !this.options.disableOneFingerPan) 
        ? 'panY' : 'rotate';
    }

    if (this.gestureMode === 'rotate') {
      const direction = this.options.invertRotation ? -1 : 1;
      this.dragVel.x = deltaX * this.options.rotationSpeed * direction;
    } else if (this.gestureMode === 'panY') {
      const depth = this.getCameraDepth();
      const worldPerPixel = this.getWorldYPerPixel(depth) * this.options.panSensitivity;
      this.contentNode.position.y -= deltaY * worldPerPixel;
      this.dragVel.set(0, 0);
    }

    this.dragPos.copyFrom(current);
  }

  private handleTwoFingerGesture(): void {
    if (!this.contentNode) return;

    const [a, b] = Array.from(this.activePointers.values());
    const pinchDist = Vector2.Distance(a, b);

    // Pinch scale
    if (!this.options.disableScale && this.initialScale && this.initialPinchDistance > 0) {
      const ratio = pinchDist / this.initialPinchDistance;
      const targetScale = this.initialScale.x * ratio;
      const clamped = Math.max(this.options.minScale, 
                               Math.min(this.options.maxScale, targetScale));
      this.contentNode.scaling.setAll(clamped);
    }

    // Two-finger pan Y
    const midY = (a.y + b.y) * 0.5;
    if (!this.options.disableTwoFingerPan && this.lastMidpointY !== null) {
      const depth = this.getCameraDepth();
      this.contentNode.position.y += (this.lastMidpointY - midY) * this.getWorldYPerPixel(depth);
    }
    this.lastMidpointY = midY;
    this.dragging = false;
  }

  // ─────────────────────────────────────────────────────────────
  // Inertia & Helpers
  // ─────────────────────────────────────────────────────────────

  private updateInertia(): void {
    if (!this.contentNode) return;

    const now = performance.now();
    const dt = this.lastUpdateTime ? Math.max(0, (now - this.lastUpdateTime) / 1000) : 0;
    this.lastUpdateTime = now;

    const timeScale = dt > 0 ? dt / (1 / 60) : 1;

    if (this.dragging) {
      const ease = Math.min(1, Math.max(0, VELOCITY_EASE * timeScale));
      this.dragVelEased.x += (this.dragVel.x - this.dragVelEased.x) * ease;
    } else {
      const decay = Math.min(1, Math.max(0, VELOCITY_DECAY * timeScale));
      this.dragVelEased.x -= this.dragVelEased.x * decay;
      if (Math.abs(this.dragVelEased.x) < VELOCITY_THRESHOLD) this.dragVelEased.x = 0;
    }

    if (!this.options.disableRotation && this.dragVelEased.x !== 0) {
      // Rotate only the nodes in rotatableNodes (no fallback - empty means no rotation)
      for (const node of this.rotatableNodes) {
        if (node) node.rotation.y += this.dragVelEased.x;
      }
      
      // Emit rotation event
      const rotationDelta = Math.abs(this.contentNode.rotation.y - this.lastRotationY);
      if (rotationDelta > EVENT_THRESHOLD_ROTATION) {
        appEvents.emit('content-rotation-changed', this.contentNode.rotation.y);
        this.lastRotationY = this.contentNode.rotation.y;
      }
    }
  }

  private getCameraDepth(): number {
    const cam = this.scene?.activeCamera as any;
    if (!cam || !this.contentNode) return 1;

    const forward = cam.getForwardRay?.().direction;
    if (!forward) return 1;

    const camToModel = this.contentNode.getAbsolutePosition().subtract(cam.position);
    return Math.max(0.001, Math.abs(Vector3.Dot(camToModel, forward.normalize())));
  }

  private getWorldYPerPixel(depth: number): number {
    const cam = this.scene?.activeCamera as any;
    if (!cam || !this.scene) return 0.002;
    
    const fov = cam.fov ?? Math.PI / 3;
    const viewportHeight = this.scene.getEngine().getRenderHeight(true);
    return (2 * depth * Math.tan(fov / 2)) / Math.max(1, viewportHeight);
  }
}
